import { useQuery } from '@apollo/react-hooks'
import {
  IonButton,
  IonContent,
  IonHeader,
  IonIcon,
  IonLoading,
  IonPage,
  IonProgressBar,
  IonTitle,
  IonToolbar
} from '@ionic/react'
import { happyOutline, thumbsUpOutline, timeOutline } from 'ionicons/icons'
import React, { useState } from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import Note from '../../components/note'
import { pdfDownloadAction } from '../../components/pdf.download'
import {
  QUOTE_DOWNLOAD_QUERY,
  QuoteDownloadData,
  QuoteDownloadVariables
} from '../../graphql/query/quote.download'
import {
  QUOTE_STATUS_QUERY,
  QuoteStatusData,
  QuoteStatusEntry,
  QuoteStatusVariables
} from '../../graphql/query/quote.status'
import { client } from '../../lib/client'
import { State } from '../../store'
import { updateHistoryEntry, updateHistoryEntryType } from '../../store/history'
import { addNotification, addNotificationType } from '../../store/notification'
import { addMessage, addMessageType } from '../../store/peter'
import { reset, resetType } from '../../store/quote'

interface StateProps {
  lastScan?: number
  accessKey: string
}

interface DispatchProps {
  updateHistoryEntry: updateHistoryEntryType
  addNotification: addNotificationType
  addMessage: addMessageType

  reset: resetType
}

type Props = RouteComponentProps<{id: string}> & DispatchProps & StateProps

const Quote: React.FC<Props> = props => {
  const [showLoading, setShowLoading] = useState(false);
  const [data, setData] = useState<QuoteStatusEntry>({status: 'waiting'})
  const [pollInterval, setPollInterval] = useState(1000)

  const {loading, error} = useQuery<QuoteStatusData, QuoteStatusVariables>(QUOTE_STATUS_QUERY, {
    variables: {
      id: props.match.params.id,
      accessKey: props.accessKey,
    },
    pollInterval: pollInterval,
    notifyOnNetworkStatusChange: true,
    onError() {
      props.addNotification({
        message: 'Wir konnten das Angebot nicht finden',
        color: 'danger',
      })

      props.history.push({ pathname: '/home' })
    },
    onCompleted(response) {
      setData(response.quote)

      props.updateHistoryEntry(response.quote)

      if (response.quote.status === 'wait_for_signature') {
        props.reset()
        setPollInterval(0)
      }

      if (response.quote.gf) {
        props.addMessage({
          animation: 'pointLeft',
          message: 'Das Angebot steht bereit!'
        })
      }
    }
  })

  const states = [
    'open_form',
    'confirm_products',
    'fill_base_data',
    'fill_contract',
    'printing',
    'wait_for_signature',
  ]

  const download = async () => {
    setShowLoading(true)
    const response = await client.query<QuoteDownloadData, QuoteDownloadVariables>({
      query: QUOTE_DOWNLOAD_QUERY,
      variables: {
        id: props.match.params.id,
        accessKey: props.accessKey,
      }
    })
    setShowLoading(false)

    await pdfDownloadAction(
      response.data.base64,
      `${data.gf}.pdf`
    )
  }

  const progress = (states.indexOf(data.status) + 1) / states.length
  let items: JSX.Element[]

  if (error || data.status === 'error') {
    items = [
      <IonIcon
        key={'check-icon'}
        icon={timeOutline}
        style={{
          fontSize: 160,
          display: 'block',
          marginLeft: 'auto',
          marginRight: 'auto',
          marginTop: 32,
        }}
      />,
      <h1
        key={'check-header'}
        style={{
          textAlign: 'center',
        }}
      >
        Verarbeitung stockt
      </h1>,
      <IonProgressBar
        key={'check-progess'}
        color="tertiary"
        type="indeterminate"
        value={progress}
        style={{
          height: 20,
          marginTop: 30,
        }}
      />,
      <p
        key={'check-note'}
        style={{
          textAlign: 'center',
          padding: 16,
        }}
      >
        Sie bekommen eine E-Mail sobald das Angebot bereit steht.
      </p>
    ]

    if (props.lastScan) {
      items.push(
        <IonButton key={'restart'} href={'/summary'}>Zurück zur Anfrage</IonButton>
      )
    }
  } else if (data.gf) {
    items = [
      <IonIcon
        key={'check-icon'}
        icon={thumbsUpOutline}
        style={{
          fontSize: 160,
          display: 'block',
          marginLeft: 'auto',
          marginRight: 'auto',
          marginTop: 32,
        }}
      />,
      <h2
        key={'gf'}
        style={{ textAlign: 'center' }}
      >
        Geschäftsfall: {data.gf}
      </h2>,
      <Note key={'signature'}>
        Sie können nun das Angebot herunterladen
      </Note>,

      <IonButton
        expand={'full'}
        size={'large'}
        key={'download'}
        onClick={() => download()}
      >
        Angebot anzeigen
      </IonButton>,

      <IonButton
        key={'action'}
        expand={'full'}
        fill={'clear'}
        color={'tertiary'}
        onClick={() => props.history.push({ pathname: '/home' })}
        style={{
          marginTop: 16,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        zur Übersicht
      </IonButton>,
    ]
  } else  {
    items = [
      <IonIcon
        key={'check-icon'}
        icon={happyOutline}
        style={{
          fontSize: 160,
          display: 'block',
          marginLeft: 'auto',
          marginRight: 'auto',
          marginTop: 32,
        }}
      />,
      <h1
        key={'check-header'}
        style={{
          textAlign: 'center',
        }}
      >
        Verarbeitung läuft
      </h1>,
      <IonProgressBar
        key={'check-progess'}
        color="tertiary"
        type="indeterminate"
        value={progress}
        style={{
          height: 20,
          marginTop: 30,
        }}
      />,
      <p
        key={'check-note'}
        style={{
          textAlign: 'center',
          padding: 16,
        }}
      >
        Sie bekommen eine E-Mail sobald das Angebot bereit steht.
      </p>
    ]
  }

  return (
    <IonPage color={data.status === 'error' ? 'danger' : 'primary'}>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        message={'PDF wird geladen...'}
      />
      <IonHeader>
        <IonToolbar color={'primary'}>
          <IonTitle>Eigenheim Versicherung</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent
        className="ion-padding"
        color={'background'}
      >
        {items}
      </IonContent>
    </IonPage>
  )
}

export default connect<StateProps, DispatchProps, {}, State>(
  ({quote, history}, props: any) => ({
    lastScan: quote.created,
    accessKey: history.list[props.match.params.id]?.accessKey || 'legacy',
  }),
  {
    updateHistoryEntry,
    reset,
    addNotification,
    addMessage,
  }
)(Quote)
